import React, { cloneElement, useEffect, useRef, useState } from 'react';
import { Button } from '../Button';
import { Link } from '../Link';
import { ProgressBar } from '../ProgressBar';
import { SubmitButton } from '../SubmitButton';
import {
    footerCss,
    addAnotherCheckboxCss,
    nextContainerCss,
    mainCss,
    progressCss,
    rootCss
} from './ResponsiveLayout.styles';
import { LabeledIconChecked } from '../LabeledIconChecked';
import { PlusCircleDuotone } from 'emerald-icons/dist/PlusCircleDuotone';
import { Footer } from 'grommet';

/**
 * Sets the height of the `<html>` element to the viewport height.
 * On some mobile browsers, 100vh is bigger than the bounds of the viewport.
 * @link https://css-tricks.com/the-trick-to-viewport-units-on-mobile/
 */
function setHtmlHeightBasedOnViewport() {
    setTimeout(() => {
        document.documentElement.style.setProperty('height', `${window.innerHeight}px`);
    }, 10);
}

export function ResponsiveLayout({
    addAnother,
    backUrl,
    banner,
    buttonTitle,
    children,
    hideNextButton,
    isLoading,
    nextButtonText,
    onSubmit,
    percent,
    showLogo,
    ...otherProps
}) {
    const [linkStyles, setLinkStyles] = useState({});
    const [progressBarStyles, setProgressBarStyles] = useState({});
    const [submitStyles, setSubmitStyles] = useState({});

    useEffect(() => {
        setHtmlHeightBasedOnViewport();
        window.addEventListener('resize', setHtmlHeightBasedOnViewport);
    }, []);

    useEffect(() => {
        setLinkStyles({ height: '8.5rem', visibility: backUrl ? 'visible' : 'hidden' });
        setProgressBarStyles({
            visibility: percent ? 'visible' : 'hidden',
        });
        setSubmitStyles({
            height: '8.5rem',
            justifyContent: addAnother ? 'space-between' : 'flex-end',
            marginLeft: addAnother ? '12rem' : '',
            visibility: hideNextButton ? 'hidden' : 'visible'
        });
    }, [addAnother, backUrl, hideNextButton, percent]);

    // has a setDisabled property exposed from SubmitButton, which we pass as a prop to the children
    const submitButtonRef = useRef();

    const shouldHideHeaderAndFooter = !backUrl && !percent && hideNextButton;

    let childrenToRender;
    if (Array.isArray(children)) {
        childrenToRender = children.map((child) => child && cloneElement(child, { submitButtonRef }));
    } else {
        childrenToRender = children && cloneElement(children, { submitButtonRef });
    }

    return (
        <div
            css={rootCss}
            {...otherProps}
        >
            {banner}
            <main css={mainCss}>
                {childrenToRender}
            </main>
            {!shouldHideHeaderAndFooter && (
                <Footer
                    css={footerCss}
                    // eslint-disable-next-line react/no-unknown-property
                    displayNone={shouldHideHeaderAndFooter}
                >
                    <Link
                        component={Button}
                        href={backUrl || '/'}
                        style={linkStyles}
                        variant='outline'
                    >
                        Back
                    </Link>
                    <ProgressBar
                        css={progressCss}
                        percent={percent}
                        style={progressBarStyles}
                    />
                    <div css={nextContainerCss}>
                        {addAnother && (
                            <LabeledIconChecked
                                css={addAnotherCheckboxCss}
                                icon={PlusCircleDuotone}
                                inputId='addAnother'
                                label='Add Another'
                            />
                        )}
                        <SubmitButton
                            isLoading={isLoading}
                            ref={submitButtonRef}
                            style={{ ...submitStyles }}
                            title={buttonTitle}
                        >
                            {nextButtonText}
                        </SubmitButton>
                    </div>
                </Footer>
            )}
        </div>
    );
}
